import {
  FluidAnchorPosition,
  FluidBreakpointName,
  FluidDisplayDensity,
  FluidElementState,
  FluidInPageNavConfig,
  FluidKeyedObject,
  FluidLocationStrategy,
  FluidMenuItem,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Host,
  Listen,
  Prop,
  readTask,
  State,
  Watch,
} from '@stencil/core';
import { Subscription } from 'rxjs';

import {
  HEADER_ENTRY,
  IN_PAGE_NAV_STANDALONE,
} from '../../../constants/constants';
import { ElementRegistry } from '../../../utils/elements/element-registry.utils';
import {
  FluidMediaState,
  ResponsiveUtils,
} from '../../../utils/resize/responsive-utils';
import { Utils } from '../../../utils/utils';
import {
  getScrollTargetIdFromURL,
  getScrollTargets,
  parseMenuItems,
  queryScrollTarget,
} from './utils/fluid-in-page-nav.utils';

/**
 * @displayName In-Page Nav
 *
 * @contextData FLUID In-Page Nav helps users to understand the contents of a page at a glance, as well as navigate to specific sections of a page via "jump links". The component features a "sticky" Tree Menu positioned on one side of the page containing a list of "jump links" that outline the page's content. Clicking a link will smoothly scroll the page to the corresponding section.
 * <br/><br/>
 * <strong>NOTE: In-Page Nav is meant to be implemented via FLUID Page, and should NOT be used as a standalone component.</strong>
 * <br/><br/>
 * NOTE: In-Page Nav works best when its "jump links" correspond to large sections of content within the page. When constructing the page's layout, it's recommended to wrap each section of content with a FLUID Container that has a meaningful "id" attribute applied to it. Each FLUID Container "id" attribute should then be set as the "href"/"route" property of a menu item in In-Page Nav's "menuItems" Prop.
 */
@Component({
  tag: 'fluid-in-page-nav',
  styleUrl: 'fluid-in-page-nav.scss',
  shadow: true,
})
export class FluidInPageNav implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * RxJS subscription to global "media state" (monitors viewport size).
   */
  private _mediaStateSubscription$: Subscription;

  /**
   * RxJS subscription to global Element Registry. Allows In-Page nav to be
   * aware of other "sticky" components' positions within the DOM.
   * Stored as a variable to allow unsubscribing when component is
   * disconnected/removed from DOM.
   */
  private _registrySubscription$: Subscription;

  /**
   * Reference to the <aside> element that contains In-Page Nav.
   */
  private _containerRef: HTMLElement;

  /**
   * Reference to targeted HTML elements
   */
  private _scrollTargetRefs: HTMLElement[];

  /**
   * ID of the current scroll target element
   */
  private _currentScrollTargetId: string;

  /**
   * Reference to tree menu
   */
  private _treeMenuRef: HTMLFluidTreeMenuElement;

  /**
   * Boolean that indicates whether the embedded Tree Menu overflows
   * the height of In-Page Nav (i.e. it can scroll). Used to determine
   * whether the Tree Menu should auto-scroll when an item is
   * programmatically selected via the scroll spy.
   */
  private _treeMenuYOverflow = false;

  /**
   * Monitors which scroll target element is currently visible
   * within the viewport.
   */
  private _visibilityObserver: IntersectionObserver;

  /**
   *The "extra vh" value that we add to _offsetTop
   */
  private _scrollOffset = 5;

  /**
   *The "base" value used to set IntersectionObserver's "rootMargin"
   */
  private _rootMarginOffset: number;

  /**
   * Whether the scroll spy is enabled or disabled. Defaulted to false,
   */
  private _disableScrollSpy = false;

  /**
   * Configurable array of item objects all containing a label and an href property
   */
  private _menuItems: FluidMenuItem[];

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidInPageNavElement;

  // ================================================================ //
  // -- States

  /**
   * Offset distance from top of viewport to the top of an In page nav
   */
  @State() _offsetTop = 0;

  /**
   * Whether In-Page Nav is hidden or visible. Used with "showAtBreakpoint"
   * Prop to hide component when viewport is smaller than the specified
   * breakpoint.
   */
  @State() _hidden = false;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Custom string title of in-page-nav element
   */
  @Prop({ mutable: true }) navTitle = 'Table of Contents';

  /**
   * Array of FluidMenuItem objects used to populate the In-Page Nav.
   * Each menu item MUST contain a "label" property AND either an "href"
   * OR "route" property. The "href"/"route" property's value should match
   * the "id" attribute of a section within the page (with or without
   * leading "#" character).
   */
  @Prop({ mutable: true }) menuItems: FluidMenuItem[];

  @Watch('menuItems')
  onMenuItemsChange(newItems: FluidMenuItem[], oldItems: FluidMenuItem[]) {
    if (Utils.isEqual(newItems, oldItems)) return;
    this.initializeMenuItems();
    this.getScrollTargets();
  }

  /**
   * Determines how In-Page Nav updates the window's location URL when a
   * "jump link" is clicked. The "hash" strategy (default) updates the URL's
   * hash (e.g. "...lmig.com#section-1"), while the "query" strategy
   * updates the URL's query string (e.g. "...lmig.com?section=section-1").
   */
  @Prop({ mutable: true }) locationStrategy: FluidLocationStrategy =
    FluidLocationStrategy.HASH;

  @Watch('locationStrategy')
  onLocationStrategyChange() {
    this.initializeMenuItems();
    this.getScrollTargets();
  }

  /**
   * If "locationStrategy" is set to "query", this Prop determines
   * the query parameter name that will be used to update the URL.
   */
  @Prop({ mutable: true }) queryStringKey = 'section';

  /**
   * By default, In-Page Nav will automatically scroll to the element
   * specified by ID in the window's location URL (if one exists) when
   * the component is loaded. Setting this Prop to "true" will disable
   * this behavior.
   */
  @Prop({ mutable: true }) disableAutoScroll: boolean;

  /**
   * Determines which side of the page that In page nav will be positioned.
   */
  @Prop({ mutable: true }) anchor:
    | FluidAnchorPosition.LEFT
    | FluidAnchorPosition.RIGHT;

  /**
   * Determines the breakpoint/viewport size at which In-Page Nav will be
   * visible (component will be hidden at sizes smaller than the specified
   * breakpoint to conserve space). Prop accepts the following
   * FluidBreakpointName values: "sm" (viewport sizes: ≥480px),
   * "md" (viewport sizes: ≥768px), "lg" (viewport sizes: ≥992px),
   * "xl" (viewport sizes: ≥1200px).
   */
  @Prop({ mutable: true }) showAtBreakpoint: Exclude<
    FluidBreakpointName,
    FluidBreakpointName.EXTRA_SMALL
  > = FluidBreakpointName.MEDIUM;

  @Watch('showAtBreakpoint')
  updateVisibleBreakpoint() {
    this.updateVisibility();
  }

  /**
   * Allows In page nav's Props to be set via a single configuration object.
   */
  @Prop({ mutable: true }) config: FluidInPageNavConfig;

  @Watch('config')
  onConfigChange() {
    Utils.selectiveMapConfigToLocal(this, this.config);
  }

  // ================================================================ //
  // -- Events

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config);
    Utils.setGlobalTheme(this);
    this.validateInstance();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action initializeTreeMenuConfig - merge "menuItems" & "treeMenuConfig" Prop values
   * @action watchMedia - initializes watcher for changes in viewport size
   */
  componentWillLoad(): void {
    this.subscribeToElementRegistry();
    this.watchMedia();
    this.initializeMenuItems();
    this.getScrollTargets();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {
    this.initializeVisibilityObserver();

    // If "disableAutoScroll" is true, "_currentScrollTargetId" will be undefined
    queryScrollTarget(
      this.pageRoot,
      this._currentScrollTargetId
    )?.scrollIntoView();

    readTask(() => {
      Utils.nextTick().then(() => {
        this._treeMenuYOverflow =
          this._containerRef?.scrollHeight > this._containerRef?.clientHeight;
      });
    });
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action unsubscribe - unsubscribes from Element Registry BehaviorSubject
   */
  disconnectedCallback() {
    this.setScrollMargins(false);
    this.removeVisibilityObserver();

    this._registrySubscription$?.unsubscribe();
    delete this._registrySubscription$;

    this._mediaStateSubscription$?.unsubscribe();
    delete this._mediaStateSubscription$;

    delete this._scrollTargetRefs;
    delete this._treeMenuRef;
    delete this._menuItems;
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * Event listener for "scrollend" events that occur when the user
   * stops scrolling. Used to set Tree Menu's selected item to either
   * the first or last scroll target element, depending on the user's
   * scroll position. Prevents the selected menu item from getting
   * "stuck" when the first/last "scroll target" doesn't/can't hit the
   * IntersectionObserver's "rootMargin" threshold.
   */
  @Listen('scrollend', { target: 'window' })
  onScrollEnd() {
    this._disableScrollSpy = false;

    Utils.nextTick().then(() => {
      const scrollPosition = window.scrollY;

      if (scrollPosition < this._rootMarginOffset) {
        this._currentScrollTargetId = this._scrollTargetRefs[0]?.id;
        const selectedId = `nav-${this._currentScrollTargetId}`;
        this._treeMenuRef?.setSelectedItem(
          selectedId,
          !this._treeMenuYOverflow
        );
      } else if (
        window.innerHeight + scrollPosition >=
        document.documentElement.scrollHeight
      ) {
        this._currentScrollTargetId = this._scrollTargetRefs.at(-1).id;
        const selectedId = `nav-${this._currentScrollTargetId}`;
        this._treeMenuRef?.setSelectedItem(
          selectedId,
          !this._treeMenuYOverflow
        );
      }
    });
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * First, sets "_currentScrollTargetId" from the window location URL
   * IF it contains a "scroll target" ID AND "disableAutoScroll" is false.
   * Then, parses the "menuItems" Prop into the format needed to create
   * "jump links" in the embedded Tree Menu.
   */
  initializeMenuItems(): void {
    if (!this.disableAutoScroll) {
      this._currentScrollTargetId = getScrollTargetIdFromURL(
        this.pageRoot,
        this.locationStrategy,
        this.queryStringKey
      );
    }
    this._menuItems = parseMenuItems(
      this.menuItems,
      this._currentScrollTargetId
    );
  }

  /**
   * Populates "_scrollTargetRefs" with references to all valid "scroll
   * target" elements specified in "_menuItems", then triggers update
   * to the scroll margins of the target elements.
   */
  getScrollTargets(): void {
    this._scrollTargetRefs = getScrollTargets(this.host, this._menuItems);
    this.setScrollMargins();
  }

  /**
   * Sets a top "scroll margin" on each "scroll target" element. The
   * "scroll margin" creates an offset from the top of the viewport &
   * compensates for "sticky" Header's height (if one exists).
   * @param enabled - whether to add or remove the scroll margin styles
   */
  setScrollMargins(enabled = true): void {
    const scrollMargin = `calc(${this._offsetTop}px + ${this._scrollOffset}vh)`;
    this._scrollTargetRefs?.forEach((el: HTMLElement) => {
      !!el && (el.style['scrollMarginTop'] = enabled ? scrollMargin : '');
    });
  }

  /**
   * Hides or shows In-Page Nav based on the current viewport size &
   * the "showAtBreakpoint" Prop's value. If the viewport size is smaller
   * than the Prop's specified breakpoint, the component will be hidden.
   * @param mediaState - current media state (object containing a MediaQueryList for each breakpoint)
   */
  updateVisibility(
    mediaState: FluidMediaState = ResponsiveUtils.mediaState
  ): void {
    const { breakpointSm, breakpointMd, breakpointLg, breakpointXl } =
      mediaState;

    switch (this.showAtBreakpoint) {
      case FluidBreakpointName.SMALL:
        this._hidden = !breakpointSm.matches;
        break;
      case FluidBreakpointName.MEDIUM:
        this._hidden = !breakpointMd.matches;
        break;
      case FluidBreakpointName.LARGE:
        this._hidden = !breakpointLg.matches;
        break;
      case FluidBreakpointName.EXTRA_LARGE:
        this._hidden = !breakpointXl.matches;
        break;
      default:
        this._hidden = !breakpointMd.matches;
    }
  }

  /**
   * Connects In-Page Nav to ResponsiveUtils, which queries the viewport to
   * determine the consuming device's size. Changes in viewport size trigger
   * calls to "updateVisibility()", which hides or shows the component based
   * on the "showAtBreakpoint" Prop's value.
   */
  watchMedia(): void {
    this._mediaStateSubscription$ = ResponsiveUtils.mediaState$.subscribe(
      (mediaState: FluidMediaState) => this.updateVisibility(mediaState)
    );
  }

  /**
   * Subscribes to the Element Registry, then watches for "sticky" Header
   * to be added to the Registry. If a sticky Header is found, sets the
   * "_offsetTop" to the Header's height & updates scroll spy.
   */
  subscribeToElementRegistry(): void {
    this._registrySubscription$ = ElementRegistry.registry$.subscribe(
      (registry: FluidKeyedObject<FluidElementState>) => {
        let offsetTop;

        if (registry[HEADER_ENTRY]?.dataStore?.sticky) {
          offsetTop = registry[HEADER_ENTRY]?.domRect?.height ?? 0;
        } else {
          offsetTop = 0;
        }

        if (this._offsetTop !== offsetTop) {
          this._offsetTop = offsetTop;
          this.setScrollMargins();
          this.initializeVisibilityObserver();
        }
      }
    );
  }

  /**
   * Initializes IntersectionObserver that monitors which scroll target
   *  element is visible in the viewport.
   */
  initializeVisibilityObserver(): void {
    if (!this._scrollTargetRefs?.length) return;

    this.removeVisibilityObserver();

    const viewportHeight = window.innerHeight;
    const vhUnit = Math.ceil(viewportHeight / 100);
    this._rootMarginOffset = this._offsetTop + vhUnit * this._scrollOffset;

    this._visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          if (!this._disableScrollSpy) {
            this._currentScrollTargetId = entry.target.id;
            const selectedId = `nav-${this._currentScrollTargetId}`;
            this._treeMenuRef?.setSelectedItem(
              selectedId,
              !this._treeMenuYOverflow
            );
          } else if (entry.target.id === this._currentScrollTargetId) {
            this._disableScrollSpy = false;
          }
        });
      },
      {
        rootMargin: `-${this._rootMarginOffset}px 0px -${
          viewportHeight - this._rootMarginOffset
        }px 0px`,
      }
    );

    this._scrollTargetRefs.forEach((target: HTMLElement) => {
      if (target && target instanceof HTMLElement) {
        this._visibilityObserver.observe(target);
      }
    });
  }

  /**
   * Removes IntersectionObserver when InPageNav is disconnected from DOM.
   */
  removeVisibilityObserver(deleteObserver = false): void {
    if (!this._visibilityObserver || !this._scrollTargetRefs?.length) return;
    this._scrollTargetRefs.forEach((elem) => {
      this._visibilityObserver?.unobserve(elem);
    });
    this._visibilityObserver?.disconnect();
    if (deleteObserver) {
      delete this._visibilityObserver;
    }
  }

  /**
   * Scrolls the "scroll target" element associated with the selected
   * menu item into view, then updates the window location URL. Called
   * each time a menu item is clicked.
   * @param event - event containing menu item associated with the area clicked
   */
  handleTreeMenuClick(event: CustomEvent<FluidMenuItem>): void {
    // Prevent ancestor event listeners / application router from picking up Tree Menu's events
    event.stopImmediatePropagation();
    // "data" property is configured to store "clean" target ID string (without "#")
    const targetId = event.detail.data;

    // Disable scroll spy while the page is auto-scrolling (if needed)
    if (this._currentScrollTargetId !== targetId) {
      this._disableScrollSpy = true;
      this._currentScrollTargetId = targetId;
    } else {
      this._disableScrollSpy = false;
    }

    // Scroll the target element into view
    this._scrollTargetRefs
      .find((ref) => ref.id === targetId)
      ?.scrollIntoView({ behavior: 'smooth' });

    // Update the window location hash/query string & push to history stack
    const url = new URL(window.location.href);
    this.locationStrategy === FluidLocationStrategy.HASH
      ? (url.hash = targetId)
      : url.searchParams.set(this.queryStringKey, targetId);
    window.history.pushState({}, '', url.toString());
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    if (!this.validInstance) return;

    return (
      <Host
        class={{
          'fluid-in-page-nav--hidden': this._hidden,
        }}>
        <aside
          class={`fluid-in-page-nav theme-${this.theme}`}
          ref={(el: HTMLElement) => (this._containerRef = el)}
          style={{
            top: `${this._offsetTop}px`,
            'max-height': `calc(100vh - ${this._offsetTop}px)`,
          }}>
          <div class={`nav-body anchor-${this.anchor}`}>
            <fluid-title
              class='nav-list-title'
              level='2'
              disableGutters={true}
              theme={this.theme}
              overrideGlobalTheme={true}>
              {this.navTitle}
            </fluid-title>
            <fluid-tree-menu
              ref={(el) => (this._treeMenuRef = el)}
              menuItems={this._menuItems}
              displayDensity={FluidDisplayDensity.COMPACT}
              collapsibleSubmenus={false}
              selectableSubmenuButtons={true}
              parentScrollContainerRef={this._containerRef}
              autoScrollSelectedItem={true}
              theme={this.theme}
              overrideGlobalTheme={true}
              onMenuItemClick={(event) => {
                this.handleTreeMenuClick(event);
              }}
            />
          </div>
        </aside>
      </Host>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * @internal
   */
  @Prop({ mutable: true }) pageRoot: HTMLElement | Document = document;

  /**
   * Signifies that the component was implemented properly via FLUID Page
   * when true. If In-Page Nav is implemented as a standalone component,
   * (i.e. this Prop is false), the component won't render & a warning
   * message will be outputted to the console.
   * @internal
   */
  @Prop({ mutable: true }) validInstance = false;

  @Watch('validInstance')
  validateInstance() {
    if (this.validInstance) return;
    Utils.consoleWarn(this.host, IN_PAGE_NAV_STANDALONE);
  }
}
